import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BalanceRecord,
  QueryBalanceRecordsOptions,
  QueryBalanceRecordsResponse,
  getBalanceRecord as universalGetBalanceRecord,
  queryBalanceRecords as universalQueryBalanceRecords,
} from './payments-balances-v5-balance-record-balance-records.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

export function getBalanceRecord(
  httpClient: HttpClient
): GetBalanceRecordSignature {
  return (balanceRecordId: string, accountId: string) =>
    universalGetBalanceRecord(
      balanceRecordId,
      accountId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBalanceRecordSignature {
  /**
   * Retrieves a single balance record by ID for the specified account.
   *
   * Returns an error if the record doesn't exist or doesn't belong to the specified account.
   * @param - Balance record ID.
   * @param - ID of the Wix Payments account that owns the balance record. The record must belong to this account. Retrieve account IDs from the Accounts API.
   * @returns Retrieved balance record.
   */
  (balanceRecordId: string, accountId: string): Promise<
    NonNullablePaths<
      BalanceRecord,
      | `accountId`
      | `accountProfileId`
      | `amount.value`
      | `amount.currency`
      | `recordType`
      | `recordStatus`
      | `paymentInfo.transactionId`
      | `paymentInfo.paymentMethodType`
      | `payoutInfo.payoutId`
      | `reserveInfo.reserveHistoryItems`
      | `reserveInfo.reserveType`
      | `bookTransferInfo.bookTransferId`
      | `bookTransferInfo.transactionReference`,
      1
    >
  >;
}

export function queryBalanceRecords(
  httpClient: HttpClient
): QueryBalanceRecordsSignature {
  return (accountId: string, options?: QueryBalanceRecordsOptions) =>
    universalQueryBalanceRecords(
      accountId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryBalanceRecordsSignature {
  /**
   * Retrieves a list of up to 1000 balance records, given the provided paging, filtering, and sorting.
   *
   * Query Balance Records runs with these defaults, which you can override:
   *
   * - `query.sort[0].fieldName` is `applied_date` in `ASC` order
   * - `query.paging.limit` is `20`
   * - `query.paging.offset` is `0` (and `offset` is required when `paging` is set)
   *
   * Filter and sort field names inside `query` must be **snake_case**. They're read from
   * the `query.filter` object and aren't auto-converted. Supported filter fields:
   * `account_profile_id`, `tags`, `payout_id`, `transaction_id`, `types`, and the date
   * ranges `transaction_creation_date`, `paid`, and `created_date`. Any other field returns
   * an `Unsupported query: unknown parameter <field>` error. ID fields accept a single
   * value by equality, `tags` and `types` accept `$in`, and date-range fields accept `$gte`
   * and `$lt`. Supported `sort[i].fieldName` values: `applied_date`,
   * `payment_info.created_date`, `created_date`. At most 1 sort field is allowed.
   *
   * To list every balance record in a payout, filter by `payout_id`.
   *
   * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
   * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging),
   * and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   * @param - ID of the Wix Payments account that owns the balance records. Retrieve account IDs from the Accounts API.
   */
  (accountId: string, options?: QueryBalanceRecordsOptions): Promise<
    NonNullablePaths<
      QueryBalanceRecordsResponse,
      | `records`
      | `records.${number}.accountId`
      | `records.${number}.accountProfileId`
      | `records.${number}.amount.value`
      | `records.${number}.amount.currency`
      | `records.${number}.recordType`
      | `records.${number}.recordStatus`
      | `records.${number}.paymentInfo.transactionId`
      | `records.${number}.paymentInfo.paymentMethodType`
      | `records.${number}.payoutInfo.payoutId`
      | `records.${number}.reserveInfo.reserveType`
      | `records.${number}.bookTransferInfo.bookTransferId`
      | `records.${number}.bookTransferInfo.transactionReference`
      | `summary.total.value`
      | `summary.total.currency`
      | `summary.items`
      | `summary.items.${number}.summaryType`
      | `summary.items.${number}.summaryItemType`,
      3
    >
  >;
}

export {
  AddressLocation,
  BalanceRecord,
  BalanceRecordStatus,
  BalanceRecordTag,
  BalanceRecordType,
  BalanceRecordsSummary,
  BalanceRecordsSummaryItem,
  BalanceRecordsSummaryItemType,
  BalanceSummary,
  BlockedBalance,
  BlockedProfileBalance,
  BookTransferInfo,
  ExplicitReserve,
  FundsOnHoldBalance,
  FundsOnHoldProfileBalance,
  GetBalanceRecordRequest,
  GetBalanceRecordResponse,
  GetBalanceSummaryRequest,
  GetBalanceSummaryResponse,
  ListBalanceRecordsRequest,
  ListBalanceRecordsResponse,
  ModalData,
  Money,
  NegativeBalance,
  NegativeProfileBalance,
  Paging,
  PagingMetadata,
  PaymentInfo,
  PaymentMethodType,
  PayoutInfo,
  PayoutsBeingVerifiedBalance,
  PayoutsBeingVerifiedProfileBalance,
  PayoutsUnderMinimumAmountBalance,
  PayoutsUnderMinimumAmountProfileBalance,
  Period,
  PeriodUnit,
  ProcessingBalance,
  ProcessingProfileBalance,
  Query,
  QueryBalanceRecordsOptions,
  QueryBalanceRecordsRequest,
  QueryBalanceRecordsResponse,
  ReserveBalance,
  ReserveBalanceMotivationOneOf,
  ReserveHistoryItem,
  ReserveInfo,
  ReservePolicy,
  ReserveProfileBalance,
  ReserveType,
  ScheduledBalance,
  ScheduledProfileBalance,
  SortOrder,
  Sorting,
} from './payments-balances-v5-balance-record-balance-records.universal.js';
